import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	FormControlLabel,
	IconButton,
	Link,
	MenuItem,
	Radio,
	RadioGroup,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import { useEffect, useRef } from 'react'

import { Loader } from '@/components'
import { useAppContext } from '@/hooks'
import type {
	AclEntryDtoRead,
	Permission,
} from '@/rtkApi/mddApiSliceGeneratedV2'

import {
	ACL_PERMISSIONS,
	AclEditState,
	getAclEntryCardKey,
	getEntryPermissionChoice,
	type PermissionChoice,
	permissionToLabelKey,
} from './structureAclUtils'

export type StructureAclManageTabProps = {
	availableGroupNamesForAdd: string[]
	editState: AclEditState
	effectiveEntries: AclEntryDtoRead[]
	effectiveLoading: boolean
	entries: AclEntryDtoRead[]
	groupNames: string[]
	isDeleting: boolean
	isSaving: boolean
	onCancelEdit: () => void
	onDelete: (entry: AclEntryDtoRead) => void
	onOpenEdit: (entry: AclEntryDtoRead) => void
	onOpenParentPermissions?: () => void
	onSaveAdd: () => void
	onSaveEdit: () => void
	parentEffectiveEntries: AclEntryDtoRead[]
	parentStructureId?: number
	parentStructureName?: string
	permissionOptions: { label: string; permission: Permission }[]
	permissionState: Record<Permission, PermissionChoice>
	selectedGroupName: string
	setPermissionChoice: (p: Permission, choice: PermissionChoice) => void
	setSelectedGroupName: (name: string) => void
}

export const StructureAclManageTab = ({
	parentStructureId,
	parentStructureName,
	onOpenParentPermissions,
	effectiveLoading,
	effectiveEntries,
	entries,
	editState,
	selectedGroupName,
	setSelectedGroupName,
	permissionState,
	setPermissionChoice,
	availableGroupNamesForAdd,
	groupNames,
	parentEffectiveEntries,
	permissionOptions,
	onOpenEdit,
	onDelete,
	onCancelEdit,
	onSaveAdd,
	onSaveEdit,
	isSaving,
	isDeleting,
}: StructureAclManageTabProps) => {
	const { t } = useAppContext()
	const editPanelRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (editState?.type !== 'add') return
		const frame = requestAnimationFrame(() => {
			editPanelRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'nearest',
			})
		})
		return () => cancelAnimationFrame(frame)
	}, [editState?.type])

	return (
		<>
			<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
				{t('ACL_EFFECTIVE_ENTRIES')}
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				{t('ACL_EFFECTIVE_DESCRIPTION')}
			</Typography>
			{parentStructureId != null && onOpenParentPermissions && (
				<Box
					sx={{
						mb: 1.5,
						display: 'flex',
						alignItems: 'center',
						gap: 0.5,
						flexWrap: 'wrap',
					}}
				>
					<Typography variant="body2" color="text.secondary">
						{t('ACL_OPEN_PARENT_HINT')}
					</Typography>
					<Link
						component="button"
						variant="body2"
						onClick={onOpenParentPermissions}
						data-testid="structure-acl-link-parent-permissions"
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.5,
						}}
					>
						{parentStructureName || t('ACL_PARENT_STRUCTURE')}
						<OpenInNewIcon sx={{ fontSize: 14 }} />
					</Link>
				</Box>
			)}
			<Box
				data-testid="structure-acl-effective-section"
				sx={{
					display: 'flex',
					flexDirection: 'row',
					flexWrap: 'wrap',
					gap: 1.5,
					minHeight: 0,
					overflow: 'auto',
					mb: 3,
				}}
			>
				{effectiveLoading ? (
					<Loader loaded={false} />
				) : effectiveEntries.length > 0 ? (
					effectiveEntries.map((entry, idx) => (
						<Card
							key={getAclEntryCardKey(entry, idx)}
							variant="outlined"
							sx={{
								overflow: 'visible',
								flex: '0 1 calc(50% - 6px)',
								width: 'calc(50% - 6px)',
								minWidth: 340,
							}}
						>
							<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										flexWrap: 'wrap',
										gap: 0.75,
									}}
								>
									<Typography
										variant="subtitle2"
										sx={{ mr: 1, whiteSpace: 'nowrap' }}
									>
										{entry.groupName ?? '–'}
									</Typography>
									{ACL_PERMISSIONS.filter(
										(p) =>
											getEntryPermissionChoice(entry, p) !== 'inherit',
									).map((p) => {
										const choice = getEntryPermissionChoice(entry, p)
										const choiceLabel = t(
											choice === 'allow'
												? 'ACL_PERMISSION_ALLOW'
												: 'ACL_PERMISSION_DENY',
										)
										return (
											<Chip
												key={p}
												label={
													<>
														<Box component="span" sx={{ fontWeight: 700 }}>
															{t(permissionToLabelKey[p])}
														</Box>
														{`: ${choiceLabel}`}
													</>
												}
												size="small"
												variant="outlined"
												color={choice === 'allow' ? 'success' : 'error'}
											/>
										)
									})}
									{ACL_PERMISSIONS.every(
										(p) =>
											getEntryPermissionChoice(entry, p) === 'inherit',
									) && (
										<Typography variant="body2" color="text.secondary">
											{t('ACL_NO_EXPLICIT_PERMISSIONS')}
										</Typography>
									)}
								</Box>
							</CardContent>
						</Card>
					))
				) : (
					<Typography color="text.secondary">
						{t('ACL_NO_EFFECTIVE_ENTRIES')}
					</Typography>
				)}
			</Box>

			<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
				{t('ACL_ENTRIES')}
			</Typography>
			<Box
				data-testid="structure-acl-entries-section"
				sx={{
					display: 'flex',
					flexDirection: 'row',
					flexWrap: 'wrap',
					gap: 1.5,
					minHeight: 0,
					overflow: 'auto',
				}}
			>
				{entries.length === 0 && !editState ? (
					<Typography color="text.secondary" sx={{ py: 2 }}>
						{t('ACL_NO_ENTRIES')}
					</Typography>
				) : (
					entries.map((entry, index) => (
						<Card
							key={getAclEntryCardKey(entry, index)}
							variant="outlined"
							data-testid={`structure-acl-entry-card-${getAclEntryCardKey(entry, index)}`}
							sx={{
								overflow: 'visible',
								flex: '0 1 calc(50% - 6px)',
								width: 'calc(50% - 6px)',
								minWidth: 340,
							}}
						>
							<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'flex-start',
										justifyContent: 'space-between',
										gap: 1,
										flexWrap: 'nowrap',
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											flexWrap: 'wrap',
											gap: 0.75,
											minWidth: 0,
											flex: 1,
										}}
									>
										<Typography
											variant="subtitle2"
											sx={{ mr: 1, whiteSpace: 'nowrap' }}
										>
											{entry.groupName ?? '–'}
										</Typography>
										{ACL_PERMISSIONS.filter(
											(p) =>
												getEntryPermissionChoice(entry, p) !== 'inherit',
										).map((p) => {
											const choice = getEntryPermissionChoice(entry, p)
											const choiceLabel = t(
												choice === 'allow'
													? 'ACL_PERMISSION_ALLOW'
													: 'ACL_PERMISSION_DENY',
											)
											return (
												<Chip
													key={p}
													label={
														<>
															<Box component="span" sx={{ fontWeight: 700 }}>
																{t(permissionToLabelKey[p])}
															</Box>
															{`: ${choiceLabel}`}
														</>
													}
													size="small"
													variant="outlined"
													color={
														choice === 'allow' ? 'success' : 'error'
													}
												/>
											)
										})}
										{ACL_PERMISSIONS.every(
											(p) =>
												getEntryPermissionChoice(entry, p) === 'inherit',
										) && (
											<Typography variant="body2" color="text.secondary">
												{t('ACL_NO_EXPLICIT_PERMISSIONS')}
											</Typography>
										)}
									</Box>
									<Box sx={{ flexShrink: 0 }}>
										<IconButton
											size="small"
											title={t('EDIT')}
											onClick={() => onOpenEdit(entry)}
											disabled={!!editState}
											data-testid={`structure-acl-button-edit-${getAclEntryCardKey(entry, index)}`}
										>
											<FontAwesomeIcon icon={faPencil} />
										</IconButton>
										<IconButton
											size="small"
											color="error"
											title={t('REMOVE')}
											onClick={() => onDelete(entry)}
											disabled={isDeleting || !!editState}
											data-testid={`structure-acl-button-remove-${getAclEntryCardKey(entry, index)}`}
										>
											<FontAwesomeIcon icon={faTrash} />
										</IconButton>
									</Box>
								</Box>
							</CardContent>
						</Card>
					))
				)}
			</Box>

			{editState && (
				<Box
					ref={editPanelRef}
					data-testid="structure-acl-edit-panel"
					sx={{
						p: 2,
						border: 1,
						borderColor: 'divider',
						borderRadius: 1,
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						minWidth: 0,
						flexShrink: 0,
					}}
				>
					<Typography variant="subtitle2">
						{editState.type === 'add'
							? t('ADD_ACL_ENTRY')
							: t('ACL_EDIT_ENTRY')}
					</Typography>
					{editState.type === 'add' && parentEffectiveEntries.length > 0 && (
						<Typography variant="caption" color="text.secondary">
							{t('ACL_ADD_PREFILL_FROM_PARENT')}
						</Typography>
					)}
					<TextField
						select
						label={t('GROUP')}
						value={selectedGroupName}
						onChange={(e) => setSelectedGroupName(e.target.value)}
						disabled={editState.type === 'edit'}
						size="small"
						sx={{ minWidth: 200 }}
						slotProps={{
							select: {
								MenuProps: {
									disableScrollLock: true,
									disableEnforceFocus: true,
									slotProps: {
										paper: {
											sx: (theme) => ({
												zIndex: theme.zIndex.modal + 100,
											}),
										},
									},
								},
							},
						}}
						data-testid="structure-acl-select-group"
					>
						<MenuItem value="">
							<em>{t('ACL_SELECT_GROUP')}</em>
						</MenuItem>
						{(editState.type === 'add'
							? availableGroupNamesForAdd
							: groupNames
						).map((name) => (
							<MenuItem key={name} value={name}>
								{name}
							</MenuItem>
						))}
					</TextField>
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ mb: 0.5 }}
						>
							{t('ACL_PERMISSION_SETTING')}
						</Typography>
						<TableContainer sx={{ overflow: 'auto', maxWidth: '100%' }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 600 }}>
											{t('ACL_PERMISSION')}
										</TableCell>
										<TableCell sx={{ fontWeight: 600 }} colSpan={3}>
											{t('ACL_PERMISSION_SETTING')}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{permissionOptions.map(({ permission, label }) => (
										<TableRow key={permission}>
											<TableCell>{label}</TableCell>
											<TableCell colSpan={3} padding="none" sx={{ py: 0.5 }}>
												<RadioGroup
													row
													aria-label={label}
													name={`acl-${permission}`}
													value={permissionState[permission]}
													onChange={(e) =>
														setPermissionChoice(
															permission,
															e.target.value as PermissionChoice,
														)
													}
													sx={{
														flexWrap: 'wrap',
														gap: 1,
														alignItems: 'center',
													}}
												>
													<FormControlLabel
														value="inherit"
														control={<Radio size="small" />}
														label={t('ACL_PERMISSION_INHERIT')}
														sx={{ mr: 2, ml: 0 }}
													/>
													<FormControlLabel
														value="allow"
														control={<Radio size="small" />}
														label={t('ACL_PERMISSION_ALLOW')}
														sx={{ mr: 2, ml: 0 }}
													/>
													<FormControlLabel
														value="deny"
														control={<Radio size="small" />}
														label={t('ACL_PERMISSION_DENY')}
														sx={{ mr: 0, ml: 0 }}
													/>
												</RadioGroup>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button
							variant="contained"
							size="small"
							onClick={
								editState.type === 'add' ? onSaveAdd : onSaveEdit
							}
							disabled={isSaving}
							data-testid="structure-acl-button-save-entry"
						>
							{editState.type === 'add' ? t('ADD') : t('SAVE')}
						</Button>
						<Button
							variant="outlined"
							size="small"
							onClick={onCancelEdit}
							data-testid="structure-acl-button-cancel-entry"
						>
							{t('CANCEL')}
						</Button>
					</Box>
				</Box>
			)}
		</>
	)
}
